import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      eyebrow="// LEGAL"
      title="Terms of Service"
      lastUpdated="February 22, 2026"
    >
      <p>
        Welcome to <strong>BroadcastFlow AI</strong> (&ldquo;BroadcastFlow&rdquo;,
        &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). These Terms of Service
        (the &ldquo;Terms&rdquo;) govern your access to and use of our radio-station
        operating platform, including our web application, dashboards, AI generation
        features (text, voice, image, video, music), social publishing, billing, and
        related services (collectively, the &ldquo;Service&rdquo;). By creating an
        account or using the Service, you agree to these Terms.
      </p>

      <p>
        <strong>If you do not agree, do not use the Service.</strong> If you are
        accepting these Terms on behalf of a radio station, network, or other
        organisation, you represent that you have authority to bind that
        organisation, and &ldquo;you&rdquo; and &ldquo;your&rdquo; refer to that entity.
      </p>

      <h2>1. Eligibility and Accounts</h2>
      <p>
        You must be at least 18 years old to use the Service. You agree to provide
        accurate account information, to keep it up to date, and to protect your
        credentials. You are responsible for all activity that occurs under your
        account, including activity by team members you add.
      </p>

      <h2>2. The Service</h2>
      <h3>2.1 What we provide</h3>
      <p>
        BroadcastFlow AI is a software-as-a-service platform that helps radio stations
        plan, produce, schedule, and distribute on-air and digital content. Features
        may include AI-assisted script &amp; news generation, voice synthesis, image
        and video generation, audio imaging tools, social posting, RSS &amp; blog
        publishing, podcast distribution, multi-station management, and analytics.
      </p>
      <h3>2.2 Bring-Your-Own-Key (BYOK)</h3>
      <p>
        Some features let you connect your own third-party API credentials (e.g.
        OpenAI, Anthropic, Google AI, ElevenLabs, Stability AI, Epidemic Sound).
        When you do so, charges and usage limits are governed by your direct
        relationship with that provider. We act only as a passthrough and store
        your credentials encrypted at rest. You are responsible for keeping those
        keys valid and within your provider quotas.
      </p>
      <h3>2.3 Managed providers (credits)</h3>
      <p>
        Where indicated, BroadcastFlow operates certain AI providers on a managed
        basis using credits purchased through your subscription. Usage of managed
        providers is metered and billed against your credit balance.
      </p>

      <h2>3. Subscription, Billing &amp; Refunds</h2>
      <p>
        Paid plans renew automatically at the end of each billing cycle until
        cancelled. Payments are processed by <strong>Stripe</strong>; by subscribing,
        you also agree to Stripe&rsquo;s terms. Prices may change with 30 days&rsquo;
        notice. Except where required by law, fees are non-refundable. We may
        suspend the Service for non-payment after a reasonable cure period.
      </p>
      <p>
        Credit packs are non-refundable once consumed and may expire 12 months after
        purchase. Trial credits, if any, expire at the end of the trial.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>Generate or distribute unlawful, infringing, hateful, defamatory, fraudulent, or sexually explicit material;</li>
        <li>Impersonate any person or station, or misrepresent your affiliation with anyone;</li>
        <li>Bypass usage limits, scrape, reverse-engineer, or interfere with the operation of the Service;</li>
        <li>Use the Service to train competing AI models;</li>
        <li>Upload malware, attempt to gain unauthorised access, or probe security boundaries.</li>
      </ul>

      <h2>5. Your Content and AI Output</h2>
      <p>
        You retain ownership of the audio, scripts, images, video, station data,
        and other materials you upload (&ldquo;Your Content&rdquo;). You grant
        BroadcastFlow a worldwide, non-exclusive, royalty-free licence to host,
        store, transmit, process, and display Your Content solely to operate
        and improve the Service for you.
      </p>
      <p>
        Output produced by the Service&rsquo;s AI features (&ldquo;Output&rdquo;) is
        provided to you with the rights you obtain from the underlying provider
        (e.g., OpenAI&rsquo;s usage terms for GPT, Google&rsquo;s terms for Gemini /
        Nano Banana 2, etc.). You are responsible for reviewing such Output before
        broadcast, including for accuracy, fairness, defamation, copyright, and
        regulatory compliance. AI Output may be inaccurate or hallucinated; do not
        publish anything you have not reviewed.
      </p>

      <h2>6. Third-Party Services</h2>
      <p>
        The Service integrates with third-party platforms such as Meta, X (Twitter),
        TikTok, YouTube, LinkedIn, WhatsApp, Pinterest, Threads, Bluesky, Telegram,
        Discord, Spotify, Apple Podcasts, Stripe, Resend, Epidemic Sound, and various
        AI providers. Your use of those platforms is governed by their own terms and
        privacy policies. We are not responsible for their content or availability.
      </p>

      <h2>7. Music &amp; Sound Effects (Audio Studio)</h2>
      <p>
        Music and SFX licensing through partners such as <strong>Epidemic Sound</strong>
        is governed by the licence terms of the provider. You must hold an active,
        valid subscription with the music provider to use those tracks on-air or in
        any published media. BroadcastFlow does not grant any music licences itself.
      </p>

      <h2>8. Privacy</h2>
      <p>
        Our <a href="/privacy">Privacy Policy</a> describes how we collect, use, and
        protect personal information. By using the Service you also agree to that
        policy.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        The Service, including its software, design, trademarks, and the
        &ldquo;BroadcastFlow AI&rdquo; name, is owned by BroadcastFlow and protected
        by intellectual-property laws. Except for the limited licence to use the
        Service granted to you under these Terms, no rights are transferred to you.
      </p>

      <h2>10. Termination</h2>
      <p>
        You may close your account at any time. We may suspend or terminate your
        access if you breach these Terms, if continued service poses a security or
        legal risk, or if your account becomes inactive for an extended period.
        On termination, we may delete your account data after a reasonable retention
        window. Sections that by their nature should survive termination (e.g.
        ownership, disclaimers, limitations of liability) will survive.
      </p>

      <h2>11. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;
        WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING FITNESS FOR A
        PARTICULAR PURPOSE, MERCHANTABILITY, OR NON-INFRINGEMENT. WE DO NOT WARRANT
        THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE, OR THAT AI OUTPUT WILL
        BE ACCURATE.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, BROADCASTFLOW WILL NOT BE LIABLE
        FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR
        FOR LOST PROFITS, REVENUE, OR DATA. OUR TOTAL LIABILITY FOR ANY CLAIM
        ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE IS LIMITED TO THE
        AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO THE
        CLAIM.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless BroadcastFlow, its
        affiliates, and their personnel from any claims, damages, costs, and
        expenses (including reasonable legal fees) arising out of (a) Your Content,
        (b) your use of Output, (c) your violation of these Terms, or (d) your
        violation of any third-party rights or applicable law.
      </p>

      <h2>14. Changes to the Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes
        we will notify you (e.g. by email or an in-app notice) and update the
        &ldquo;Last updated&rdquo; date above. Continued use of the Service after
        changes take effect constitutes acceptance of the revised Terms.
      </p>

      <h2>15. Governing Law &amp; Disputes</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which
        BroadcastFlow is established, without regard to its conflict-of-laws rules.
        Any disputes will be resolved exclusively in the courts of that
        jurisdiction, except where mandatory consumer-protection law gives you the
        right to a local forum.
      </p>

      <h2>16. Contact</h2>
      <p>
        For questions about these Terms, write to <a href="mailto:legal@broadcastflow.ai">legal@broadcastflow.ai</a>.
      </p>

      <p className="text-xs text-ink-muted mt-12 pt-6 border-t border-line">
        These Terms are provided as a starting point and have not been reviewed by
        legal counsel. You should consult a qualified lawyer in your jurisdiction
        before relying on them in production.
      </p>
    </LegalLayout>
  );
}
